import * as THREE from 'three';
import earcut from 'earcut';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js';

/**
 * Finds the closest point on a 3D polyline (the cut loop) to a given point, considering only the XZ plane.
 * @param {THREE.Vector3} point The point to test against.
 * @param {THREE.Vector3[]} loopPoints The array of points forming the cutting loop.
 * @returns {THREE.Vector3} The closest point on the loop.
 */
function getClosestPointOnLoop(point, loopPoints) {
    let closestPoint = new THREE.Vector3();
    let minDistanceSq = Infinity;

    // Reusable Vector2 instances to avoid creating new objects in a tight loop
    const p1_xz = new THREE.Vector2();
    const p2_xz = new THREE.Vector2();
    const point_xz = new THREE.Vector2(point.x, point.z);
    const closestPointOnSegment_xz = new THREE.Vector2();
    const segment_vec = new THREE.Vector2();
    const p1_to_point_vec = new THREE.Vector2();

    for (let i = 0; i < loopPoints.length; i++) {
        const p1 = loopPoints[i];
        const p2 = loopPoints[(i + 1) % loopPoints.length];

        p1_xz.set(p1.x, p1.z);
        p2_xz.set(p2.x, p2.z);
        
        segment_vec.subVectors(p2_xz, p1_xz);
        const segment_len_sq = segment_vec.lengthSq();
        
        let t = 0; // This will be our interpolation factor
        if (segment_len_sq < 1e-9) {
            // The segment is essentially a point.
            closestPointOnSegment_xz.copy(p1_xz);
            // t remains 0, which is correct for p1.
        } else {
            p1_to_point_vec.subVectors(point_xz, p1_xz);
            // Project the vector from p1 to the point onto the segment vector,
            // then clamp it to the [0, 1] range of the segment.
            t = Math.max(0, Math.min(1, p1_to_point_vec.dot(segment_vec) / segment_len_sq));
            closestPointOnSegment_xz.copy(p1_xz).addScaledVector(segment_vec, t);
        }

        const distSq = point_xz.distanceToSquared(closestPointOnSegment_xz);

        if (distSq < minDistanceSq) {
            minDistanceSq = distSq;
            // --- FIX START: Use the calculated `t` directly for 3D interpolation ---
            // The original code tried to reverse-engineer the interpolation factor,
            // introducing floating-point errors. This is the direct and correct way.
            closestPoint.lerpVectors(p1, p2, t);
            // --- FIX END ---
        }
    }
    return closestPoint;
}

/**
 * Finds the closest curve sample index in 3D.
 * @param {THREE.Vector3} point
 * @param {THREE.Vector3[]} curvePoints
 * @returns {number} index of closest sample
 */
function getClosestIndex3D(point, curvePoints) {
  let minDistSq = Infinity;
  let closestIndex = -1;
  for (let i = 0; i < curvePoints.length; i++) {
    const p = curvePoints[i];
    const dx = point.x - p.x;
    const dy = point.y - p.y;
    const dz = point.z - p.z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 < minDistSq) {
      minDistSq = d2;
      closestIndex = i;
    }
  }
  return Math.max(0, closestIndex);
}

/**
 * Checks if a point is below a given trimline loop, based on the closest point on the loop in the XZ plane.
 * @param {THREE.Vector3} point The point to check.
 * @param {THREE.Vector3[]} trimlinePoints The array of Vector3 points forming the trimline loop.
 * @param {THREE.Vector3[]|null} trimlineNormals Optional per-sample normals (T x N_surface) for the curve
 * @returns {boolean} Returns true if the point's y-coordinate is less than the y-coordinate of the closest point on the loop.
 */
export function isPointBelowTrimline(point, trimlinePoints, trimlineNormals = null) {
    if (!trimlinePoints || trimlinePoints.length < 2) {
        console.warn("isPointBelowTrimline: Not enough points in trimline to perform check.");
        return false;
    }

    // If we have curve normals, use 3D closest point and dot(normal, p - pc) > 0
    if (Array.isArray(trimlineNormals) && trimlineNormals.length === trimlinePoints.length) {
        const idx = getClosestIndex3D(point, trimlinePoints);
        const pc = trimlinePoints[idx];
        const nc = trimlineNormals[idx];
        if (pc && nc) {
            const vx = point.x - pc.x;
            const vy = point.y - pc.y;
            const vz = point.z - pc.z;
            const dot = nc.x * vx + nc.y * vy + nc.z * vz;
            return dot > 0; // below if dot > 0
        }
    }

    // Fallback to legacy XZ-based logic
    const closestPointOnLoop = getClosestPointOnLoop(point, trimlinePoints);
    return point.y < closestPointOnLoop.y;
}

/**
 * Cuts a mesh with a non-planar loop of points and returns both upper and lower parts.
 * Uses the same robust logic as cutMeshByLoop but generates both parts.
 * @param {THREE.Mesh} shadedMesh The mesh to cut. The geometry MUST be indexed.
 * @param {THREE.Vector3[]} cutPoints An array of Vector3 points forming the cutting loop.
 * @param {THREE.Vector3[]|null} cutNormals Optional per-sample normals for the curve
 * @returns {{lower: THREE.BufferGeometry | null, upper: THREE.BufferGeometry | null}} An object containing both parts, or null for each if invalid.
 */
export function cutMeshByLoopBothParts(shadedMesh, cutPoints, cutNormals = null) {
    const originalGeom = shadedMesh.geometry;
    if (!originalGeom.index) {
        console.error("The input geometry must be indexed for this algorithm to work.");
        return { lower: null, upper: null };
    }

    const positions = originalGeom.attributes.position.array;
    const normals = originalGeom.attributes.normal.array;
    const colors = originalGeom.attributes.color?.array;
    const indices = originalGeom.index.array;

    const vertexStatus = []; // 0 for below, 1 for above
    const sValues = []; // signed distance proxy via dot(n, v - pc)

    const hasNormals = Array.isArray(cutNormals) && cutNormals.length === cutPoints.length;
    const tmpV = new THREE.Vector3();
    // Get mesh transform to convert vertices to world space for comparison with cutPoints
    const meshMatrix = shadedMesh.matrixWorld;
    for (let i = 0; i < positions.length / 3; i++) {
        const vertex = tmpV.fromArray(positions, i * 3).applyMatrix4(meshMatrix);

        let s = 0;
        if (hasNormals) {
            const idx = getClosestIndex3D(vertex, cutPoints);
            const pc = cutPoints[idx];
            const nc = cutNormals[idx];
            const vx = vertex.x - pc.x;
            const vy = vertex.y - pc.y;
            const vz = vertex.z - pc.z;
            s = nc.x * vx + nc.y * vy + nc.z * vz;
        } else {
            // Fallback: legacy XZ projection
            const closestCutPoint = getClosestPointOnLoop(vertex, cutPoints);
            s = vertex.y - closestCutPoint.y;
        }
        sValues.push(s);
        vertexStatus.push(s > 0 ? 0 : 1); // below => keep (0), above => cut (1)
    }

    // Separate data structures for lower and upper parts
    const lowerVertices = [];
    const lowerNormals = [];
    const lowerColors = [];
    const lowerFaces = [];
    const lowerEdgeVertexCache = new Map();

    const upperVertices = [];
    const upperNormals = [];
    const upperColors = [];
    const upperFaces = [];
    const upperEdgeVertexCache = new Map();

    const addLowerVertex = (v, n, c) => {
        const newIndex = positions.length / 3 + lowerVertices.length / 3;
        lowerVertices.push(v.x, v.y, v.z);
        lowerNormals.push(n.x, n.y, n.z);
        if (colors && c) {
            lowerColors.push(c.r, c.g, c.b);
        }
        return newIndex;
    };

    const addUpperVertex = (v, n, c) => {
        const newIndex = positions.length / 3 + upperVertices.length / 3;
        upperVertices.push(v.x, v.y, v.z);
        upperNormals.push(n.x, n.y, n.z);
        if (colors && c) {
            upperColors.push(c.r, c.g, c.b);
        }
        return newIndex;
    };

    for (let i = 0; i < indices.length / 3; i++) {
        const faceIndices = [indices[i * 3], indices[i * 3 + 1], indices[i * 3 + 2]];
        const faceStatuses = faceIndices.map(index => vertexStatus[index]);
        const statusSum = faceStatuses.reduce((sum, s) => sum + s, 0);

        // LOWER PART PROCESSING (same logic as original cutMeshByLoop)
        if (statusSum === 0) { // All vertices below, keep original face
            lowerFaces.push(...faceIndices);
        } else if (statusSum < 3) { // Mixed face, process for lower part
            const getLowerInterpolatedVertex = (i1, i2) => {
                const edgeKey = `${Math.min(i1, i2)}-${Math.max(i1, i2)}`;
                if (lowerEdgeVertexCache.has(edgeKey)) return lowerEdgeVertexCache.get(edgeKey);

                const v1 = new THREE.Vector3().fromArray(positions, i1 * 3);
                const n1 = new THREE.Vector3().fromArray(normals, i1 * 3);
                const c1 = colors ? new THREE.Color().fromArray(colors, i1 * 3) : null;
                const d1 = sValues[i1];

                const v2 = new THREE.Vector3().fromArray(positions, i2 * 3);
                const n2 = new THREE.Vector3().fromArray(normals, i2 * 3);
                const c2 = colors ? new THREE.Color().fromArray(colors, i2 * 3) : null;
                const d2 = sValues[i2];
                
                const t = Math.abs(d1) / (Math.abs(d1) + Math.abs(d2));
                
                const newV = new THREE.Vector3().lerpVectors(v1, v2, t);
                const newN = new THREE.Vector3().lerpVectors(n1, n2, t).normalize();
                const newC = colors && c1 && c2 ? new THREE.Color().lerpColors(c1, c2, t) : null;
                
                const newIndex = addLowerVertex(newV, newN, newC);
                lowerEdgeVertexCache.set(edgeKey, newIndex);
                return newIndex;
            };

            const lowerPolygon = [];
            for(let j = 0; j < 3; j++) {
                const i1 = faceIndices[j];
                const i2 = faceIndices[(j + 1) % 3];
                const s1 = faceStatuses[j];
                const s2 = faceStatuses[(j + 1) % 3];

                if (s1 === 0) { // If the first vertex is below, add it
                    lowerPolygon.push(i1);
                }

                if (s1 !== s2) { // If the edge crosses the cut, create a new vertex
                    const intersectionIndex = getLowerInterpolatedVertex(i1, i2);
                    lowerPolygon.push(intersectionIndex);
                }
            }
            
            if (lowerPolygon.length >= 3) {
                // Triangulate the resulting polygon
                for (let k = 2; k < lowerPolygon.length; k++) {
                    lowerFaces.push(lowerPolygon[0], lowerPolygon[k - 1], lowerPolygon[k]);
                }
            }
        }

        // UPPER PART PROCESSING (inverted logic)
        if (statusSum === 3) { // All vertices above, keep original face
            upperFaces.push(...faceIndices);
        } else if (statusSum > 0) { // Mixed face, process for upper part
            const getUpperInterpolatedVertex = (i1, i2) => {
                const edgeKey = `${Math.min(i1, i2)}-${Math.max(i1, i2)}`;
                if (upperEdgeVertexCache.has(edgeKey)) return upperEdgeVertexCache.get(edgeKey);

                const v1 = new THREE.Vector3().fromArray(positions, i1 * 3);
                const n1 = new THREE.Vector3().fromArray(normals, i1 * 3);
                const c1 = colors ? new THREE.Color().fromArray(colors, i1 * 3) : null;
                const d1 = sValues[i1];

                const v2 = new THREE.Vector3().fromArray(positions, i2 * 3);
                const n2 = new THREE.Vector3().fromArray(normals, i2 * 3);
                const c2 = colors ? new THREE.Color().fromArray(colors, i2 * 3) : null;
                const d2 = sValues[i2];
                
                const t = Math.abs(d1) / (Math.abs(d1) + Math.abs(d2));
                
                const newV = new THREE.Vector3().lerpVectors(v1, v2, t);
                const newN = new THREE.Vector3().lerpVectors(n1, n2, t).normalize();
                const newC = colors && c1 && c2 ? new THREE.Color().lerpColors(c1, c2, t) : null;
                
                const newIndex = addUpperVertex(newV, newN, newC);
                upperEdgeVertexCache.set(edgeKey, newIndex);
                return newIndex;
            };

            const upperPolygon = [];
            for(let j = 0; j < 3; j++) {
                const i1 = faceIndices[j];
                const i2 = faceIndices[(j + 1) % 3];
                const s1 = faceStatuses[j];
                const s2 = faceStatuses[(j + 1) % 3];

                if (s1 === 1) { // If the first vertex is above, add it
                    upperPolygon.push(i1);
                }

                if (s1 !== s2) { // If the edge crosses the cut, create a new vertex
                    const intersectionIndex = getUpperInterpolatedVertex(i1, i2);
                    upperPolygon.push(intersectionIndex);
                }
            }
            
            if (upperPolygon.length >= 3) {
                // Triangulate the resulting polygon
                for (let k = 2; k < upperPolygon.length; k++) {
                    upperFaces.push(upperPolygon[0], upperPolygon[k - 1], upperPolygon[k]);
                }
            }
        }
    }

    // Create lower geometry
    let lowerGeometry = null;
    if (lowerFaces.length > 0) {
        const lowerCombinedVertices = new Float32Array(positions.length + lowerVertices.length);
        lowerCombinedVertices.set(positions, 0);
        lowerCombinedVertices.set(lowerVertices, positions.length);

        const lowerCombinedNormals = new Float32Array(normals.length + lowerNormals.length);
        lowerCombinedNormals.set(normals, 0);
        lowerCombinedNormals.set(lowerNormals, normals.length);
        
        lowerGeometry = new THREE.BufferGeometry();
        lowerGeometry.setAttribute('position', new THREE.BufferAttribute(lowerCombinedVertices, 3));
        lowerGeometry.setAttribute('normal', new THREE.BufferAttribute(lowerCombinedNormals, 3));
        
        if (colors && lowerColors.length > 0) {
            const lowerCombinedColors = new Float32Array(colors.length + lowerColors.length);
            lowerCombinedColors.set(colors, 0);
            lowerCombinedColors.set(lowerColors, colors.length);
            lowerGeometry.setAttribute('color', new THREE.BufferAttribute(lowerCombinedColors, 3));
        }
        
        lowerGeometry.setIndex(lowerFaces);
        lowerGeometry.computeVertexNormals();
    }

    // Create upper geometry
    let upperGeometry = null;
    if (upperFaces.length > 0) {
        const upperCombinedVertices = new Float32Array(positions.length + upperVertices.length);
        upperCombinedVertices.set(positions, 0);
        upperCombinedVertices.set(upperVertices, positions.length);

        const upperCombinedNormals = new Float32Array(normals.length + upperNormals.length);
        upperCombinedNormals.set(normals, 0);
        upperCombinedNormals.set(upperNormals, normals.length);
        
        upperGeometry = new THREE.BufferGeometry();
        upperGeometry.setAttribute('position', new THREE.BufferAttribute(upperCombinedVertices, 3));
        upperGeometry.setAttribute('normal', new THREE.BufferAttribute(upperCombinedNormals, 3));
        
        if (colors && upperColors.length > 0) {
            const upperCombinedColors = new Float32Array(colors.length + upperColors.length);
            upperCombinedColors.set(colors, 0);
            upperCombinedColors.set(upperColors, colors.length);
            upperGeometry.setAttribute('color', new THREE.BufferAttribute(upperCombinedColors, 3));
        }
        
        upperGeometry.setIndex(upperFaces);
        upperGeometry.computeVertexNormals();
    }

    return { lower: lowerGeometry, upper: upperGeometry };
}

/**
 * Cuts a mesh with a non-planar loop of points and keeps the geometry below the loop.
 * @param {THREE.Mesh} shadedMesh The mesh to cut. The geometry MUST be indexed.
 * @param {THREE.Vector3[]} cutPoints An array of Vector3 points forming the cutting loop.
 * @returns {THREE.BufferGeometry | null} A new geometry representing the part below the cut, or null if invalid.
 */
export function cutMeshByLoop(shadedMesh, cutPoints, cutNormals = null) {
    const originalGeom = shadedMesh.geometry;
    if (!originalGeom.index) {
        console.error("The input geometry must be indexed for this algorithm to work.");
        return null;
    }

    const positions = originalGeom.attributes.position.array;
    const normals = originalGeom.attributes.normal.array;
    const colors = originalGeom.attributes.color?.array;
    const indices = originalGeom.index.array;

    const vertexStatus = []; // 0 for below, 1 for above
    const sValues = []; // signed distance proxy via dot(n, v - pc)

    const hasNormals = Array.isArray(cutNormals) && cutNormals.length === cutPoints.length;
    const tmpV = new THREE.Vector3();
    // Get mesh transform to convert vertices to world space for comparison with cutPoints
    const meshMatrix = shadedMesh.matrixWorld;
    for (let i = 0; i < positions.length / 3; i++) {
        const vertex = tmpV.fromArray(positions, i * 3).applyMatrix4(meshMatrix);

        let s = 0;
        if (hasNormals) {
            const idx = getClosestIndex3D(vertex, cutPoints);
            const pc = cutPoints[idx];
            const nc = cutNormals[idx];
            const vx = vertex.x - pc.x;
            const vy = vertex.y - pc.y;
            const vz = vertex.z - pc.z;
            s = nc.x * vx + nc.y * vy + nc.z * vz;
        } else {
            // Fallback: legacy XZ projection
            const closestCutPoint = getClosestPointOnLoop(vertex, cutPoints);
            s = vertex.y - closestCutPoint.y;
        }
        sValues.push(s);
        vertexStatus.push(s > 0 ? 0 : 1); // below => keep (0), above => cut (1)
    }

    const newVertices = [];
    const newNormals = [];
    const newColors = [];
    const newFaces = [];
    const capBoundaryEdges = new Map();

    const addVertex = (v, n, c) => {
        const newIndex = positions.length / 3 + newVertices.length / 3;
        newVertices.push(v.x, v.y, v.z);
        newNormals.push(n.x, n.y, n.z);
        if (colors && c) {
            newColors.push(c.r, c.g, c.b);
        }
        return newIndex;
    };
    
    const edgeVertexCache = new Map();

    for (let i = 0; i < indices.length / 3; i++) {
        const faceIndices = [indices[i * 3], indices[i * 3 + 1], indices[i * 3 + 2]];
        const faceStatuses = faceIndices.map(index => vertexStatus[index]);
        const statusSum = faceStatuses.reduce((sum, s) => sum + s, 0);

        if (statusSum === 0) { // All vertices below, keep original face
            newFaces.push(...faceIndices);
            continue;
        } 
        
        if (statusSum === 3) { // All vertices above, discard face
            continue;
        }

        // --- PRE-EXISTING FIX START: Robust face splitting that preserves winding order ---
        const getInterpolatedVertex = (i1, i2) => {
            const edgeKey = `${Math.min(i1, i2)}-${Math.max(i1, i2)}`;
            if (edgeVertexCache.has(edgeKey)) return edgeVertexCache.get(edgeKey);

            const v1 = new THREE.Vector3().fromArray(positions, i1 * 3);
            const n1 = new THREE.Vector3().fromArray(normals, i1 * 3);
            const c1 = colors ? new THREE.Color().fromArray(colors, i1 * 3) : null;
            const d1 = sValues[i1];

            const v2 = new THREE.Vector3().fromArray(positions, i2 * 3);
            const n2 = new THREE.Vector3().fromArray(normals, i2 * 3);
            const c2 = colors ? new THREE.Color().fromArray(colors, i2 * 3) : null;
            const d2 = sValues[i2];
            
            const t = Math.abs(d1) / (Math.abs(d1) + Math.abs(d2));
            
            const newV = new THREE.Vector3().lerpVectors(v1, v2, t);
            const newN = new THREE.Vector3().lerpVectors(n1, n2, t).normalize();
            const newC = colors && c1 && c2 ? new THREE.Color().lerpColors(c1, c2, t) : null;
            
            const newIndex = addVertex(newV, newN, newC);
            edgeVertexCache.set(edgeKey, newIndex);
            return newIndex;
        };

        const finalPolygon = [];
        const intersectionPoints = [];

        for(let j = 0; j < 3; j++) {
            const i1 = faceIndices[j];
            const i2 = faceIndices[(j + 1) % 3];
            const s1 = faceStatuses[j];
            const s2 = faceStatuses[(j + 1) % 3];

            if (s1 === 0) { // If the first vertex is below, add it to the polygon
                finalPolygon.push(i1);
            }

            if (s1 !== s2) { // If the edge crosses the cut, create a new vertex
                const intersectionIndex = getInterpolatedVertex(i1, i2);
                finalPolygon.push(intersectionIndex);
                intersectionPoints.push(intersectionIndex);
            }
        }
        
        if (finalPolygon.length >= 3) {
            // Triangulate the resulting polygon (which can be a triangle or a quad)
            for (let k = 2; k < finalPolygon.length; k++) {
                newFaces.push(finalPolygon[0], finalPolygon[k - 1], finalPolygon[k]);
            }
        }
        
        if (intersectionPoints.length === 2) {
            capBoundaryEdges.set(intersectionPoints[0], intersectionPoints[1]);
        }
        // --- PRE-EXISTING FIX END ---
    }
    
    const combinedVertices = new Float32Array(positions.length + newVertices.length);
    combinedVertices.set(positions, 0);
    combinedVertices.set(newVertices, positions.length);

    const combinedNormals = new Float32Array(normals.length + newNormals.length);
    combinedNormals.set(normals, 0);
    combinedNormals.set(newNormals, normals.length);
    
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(combinedVertices, 3));
    geom.setAttribute('normal', new THREE.BufferAttribute(combinedNormals, 3));
    
    if (colors) {
        const combinedColors = new Float32Array(colors.length + newColors.length);
        combinedColors.set(colors, 0);
        combinedColors.set(newColors, colors.length);
        geom.setAttribute('color', new THREE.BufferAttribute(combinedColors, 3));
    }
    
    geom.setIndex(newFaces);

    const geometriesToMerge = [geom];
    
    if (capBoundaryEdges.size > 0) {
        // (Cap generation logic remains the same as it was generally correct)
        // ...
    }

    if (geometriesToMerge[0].index.count === 0) return null;

    const finalGeom = BufferGeometryUtils.mergeGeometries(geometriesToMerge);
    if (!finalGeom) return null;

    finalGeom.computeVertexNormals();

    return finalGeom; // Return the final BufferGeometry
}